import {
    Button,
    Form,
    Table,
    Space,
    Tooltip,
    Divider,
    Popconfirm,
    message,
    Input,
} from 'antd';
import { EditTwoTone, DeleteTwoTone } from '@ant-design/icons';
import React, { useState } from 'react';
import useSWR from 'swr';
import {
    DefaultPaginationConfig,
    DEFAULT_PAGE_SIZE,
} from '../../constants/pagination';
import {
    deletePerfilBusqueda,
    PERFIL_BUSQUEDAS_ENDPOINT,
    getAllPerfilBusquedas,
} from '../../services/api/perfil-busqueda';
import {
    DELETE_CONFIRMATION_MESSAGE,
    GENERIC_ERROR_MESSAGE,
    GENERIC_SUCCESS_MESSAGE,
} from '../../constants/feedback';
import PerfilBusquedaItem from './perfil-busqueda-item';

const { Column } = Table;

const itemLayout = {
    labelCol: { span: 24 },
};

const initialFilters = {
    descripcion: '',
    sortField: 'descripcion',
    sortOrder: 'ascend',
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
};

const PerfilBusquedaList = () => {
    const [filters, setFilters] = useState({ ...initialFilters });
    const [selectedFilters, setSelectedFilters] = useState({
        ...initialFilters,
    });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(0);

    const {
        data: perfilBusquedas,
        isLoading,
        mutate,
    } = useSWR([PERFIL_BUSQUEDAS_ENDPOINT, filters], ([_, filters]) =>
        getAllPerfilBusquedas(filters)
    );

    const [form] = Form.useForm();

    const handleTableChange = (pagination, _, sorter) => {
        const params = {
            ...filters,
            pageNumber: Number(pagination.current),
            pageSize: Number(pagination.pageSize),
            sortField: sorter.columnKey,
            sortOrder: sorter.order,
        };

        setSelectedFilters({ ...params });
        setFilters({ ...params });
    };

    const getActionColumn = (perfilBusqueda) => {
        return (
            <Space size={0}>
                <Tooltip title='Editar'>
                    <EditTwoTone
                        onClick={() =>
                            handleEditClick(perfilBusqueda.idPerfilBusqueda)
                        }
                    />
                </Tooltip>

                <Divider type='vertical' />

                <Popconfirm
                    title={DELETE_CONFIRMATION_MESSAGE}
                    onConfirm={() =>
                        handleConfirmDelete(perfilBusqueda.idPerfilBusqueda)
                    }
                >
                    <Tooltip title='Eliminar' className='cursor-pointer'>
                        <DeleteTwoTone />
                    </Tooltip>
                </Popconfirm>
            </Space>
        );
    };

    const handleShowSizeChange = (current, pageSize) => {
        setSelectedFilters({
            ...filters,
            pageSize: pageSize,
        });
    };

    const handleConfirmDelete = async (id) => {
        try {
            await deletePerfilBusqueda(id);

            message.success(GENERIC_SUCCESS_MESSAGE);
            mutate();
        } catch (error) {
            if (error.response.status === 409) {
                message.error(error.response.data.Message);
            } else {
                message.error(GENERIC_ERROR_MESSAGE);
            }
        }
    };

    const handleReset = async () => {
        form.resetFields();

        setSelectedFilters({ ...initialFilters });
        setFilters({ ...initialFilters });
    };

    const handleFinish = async (values) => {
        const params = {
            ...selectedFilters,
            descripcion: values.descripcion,
            pageNumber: 1,
        };

        setFilters(params);
    };

    const handleEditClick = (id) => {
        setSelectedId(id);
        setIsModalOpen(true);
    };

    const handleNewClick = () => {
        setIsModalOpen(true);
    };

    const handleSaveModal = () => {
        mutate();
        setSelectedId(0);
        setIsModalOpen(false);
    };

    const handleCancelModal = () => {
        setSelectedId(0);
        setIsModalOpen(false);
    };

    return (
        <>
            <div className='row pt-3'>
                <div className='col-lg-12'>
                    <ul className='breadcrumb shadow-sm rounded-2'>
                        <li>
                            <h6>Perfiles de búsqueda</h6>
                        </li>
                    </ul>
                </div>

                <div className='col-lg-12'>
                    <div className='card rounded-4 shadow'>
                        <h6 className='txt-color-primary pt-3 border-bottom mx-3'>
                            Filtros
                        </h6>

                        <div className='card-body'>
                            <Form form={form} onFinish={handleFinish}>
                                <div className='row pl-2 pr-2'>
                                    <div className='col-lg-3 pt-2 pb-3'>
                                        <Form.Item
                                            name='descripcion'
                                            label='Descripción'
                                            {...itemLayout}
                                            rules={[
                                                {
                                                    whitespace: true,
                                                },
                                            ]}
                                        >
                                            <Input maxLength={50} allowClear />
                                        </Form.Item>
                                    </div>

                                    <div className='col-lg-12 text-end'>
                                        <Form.Item className='mb-0'>
                                            <Space>
                                                <Button
                                                    type='primary'
                                                    htmlType='submit'
                                                >
                                                    Filtrar
                                                </Button>
                                                <Button onClick={handleReset}>
                                                    Limpiar
                                                </Button>
                                            </Space>
                                        </Form.Item>
                                    </div>
                                </div>
                            </Form>
                        </div>
                    </div>
                </div>
            </div>

            <div className='row pt-4 pb-5'>
                <div className='col-lg-12'>
                    <div className='card rounded-4 shadow'>
                        <h6 className='txt-color-primary pt-3 border-bottom mx-3'>
                            Perfiles de búsqueda
                        </h6>

                        <div className='card-body'>
                            <div className='row pl-2 pr-2'>
                                <Table
                                    rowKey={(perfilBusqueda) =>
                                        perfilBusqueda.idPerfilBusqueda
                                    }
                                    dataSource={perfilBusquedas?.data.items}
                                    pagination={{
                                        ...DefaultPaginationConfig,
                                        total: perfilBusquedas?.data.totalCount,
                                        onShowSizeChange: handleShowSizeChange,
                                    }}
                                    onChange={handleTableChange}
                                    loading={isLoading}
                                >
                                    <Column
                                        title='Descripción'
                                        dataIndex='descripcion'
                                        key='descripcion'
                                        sorter
                                        width='80%'
                                    />

                                    <Column
                                        title='Acción'
                                        render={(perfilBusqueda) =>
                                            getActionColumn(perfilBusqueda)
                                        }
                                    />
                                </Table>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='col-lg-12 text-end mt-3'>
                    <Button type='primary' onClick={handleNewClick}>
                        Nuevo perfil de búsqueda
                    </Button>
                </div>
            </div>

            {isModalOpen && (
                <PerfilBusquedaItem
                    isModalOpen={isModalOpen}
                    idPerfilBusqueda={selectedId}
                    onSave={handleSaveModal}
                    onCancel={handleCancelModal}
                />
            )}
        </>
    );
};

export default PerfilBusquedaList;
